import { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { inject, observer } from 'mobx-react';
import Table from 'react-bootstrap/Table';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  CUSTOMER_PERMISSIONS,
  TRANSACTION_TYPES,
  WEBHOOK_TRANSFER_TYPES_FOR_CONFIG,
  WEBHOOK_ENABLED_STATUSES_OPTIONS,
  WEBHOOK_BATCH_ENABLED_STATUSES_OPTIONS
} from 'components/constants';
import i18nContext from 'components/i18n-context';
import Loader from 'components/Loader';
import Button from 'uikit/Button/Button';
import Input from 'uikit/Input/Input';
import { CheckBox } from 'uikit/CheckBox/CheckBox';
import './WebhookTab.scss';
import { FULL_ACCESS, hasRole } from '../../../services/roles';

const WebhookTab = ({
  isLoading,
  isInitialized,
  getCurrentCustomerWebhookUrlList,
  createNewCustomerWebhookUrl,
  updateCustomerWebhookUrl,
  deleteCustomerWebhookUrl,
  testWebhookUrl,
  updateWebhookConfiguration,
  customerWebhookUrlList,
  directApiCallToken,
  generateDirectApiCallToken,
  revokeDirectApiCallToken,
  checkDirectApiCallToken,
  isDirectApiCallTokenExist
}) => {
  const i18n = useContext(i18nContext);
  const webhookData =
    customerWebhookUrlList?.length > 0 && customerWebhookUrlList[0] ? customerWebhookUrlList[0] : null;
  const webhookUrlId = webhookData?.id || null;

  const [transactionRules, setTransactionRules] = useState({});
  const [batchStatuses, setBatchStatuses] = useState([]);

  useEffect(() => {
    getCurrentCustomerWebhookUrlList();
    checkDirectApiCallToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initialize transaction rules from webhook configuration
  useEffect(() => {
    if (webhookData?.webhookConfiguration?.paymentTransactionRules) {
      // Convert array from API to object: {NORMAL: ['PAID'], EXCHANGE: ['FAILED']}
      const rulesObject = webhookData.webhookConfiguration.paymentTransactionRules.reduce((acc, rule) => {
        acc[rule.transferType] = rule.enabledStatuses || [];
        return acc;
      }, {});
      setTransactionRules(rulesObject);
    } else {
      setTransactionRules({});
    }
  }, [webhookData]);

  // Initialize batch statuses from webhook configuration
  useEffect(() => {
    if (webhookData?.webhookConfiguration?.paymentBatchRule?.enabledStatuses) {
      setBatchStatuses(webhookData.webhookConfiguration.paymentBatchRule.enabledStatuses);
    } else {
      setBatchStatuses([]);
    }
  }, [webhookData]);

  const form = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues: {
      webhookUrl: webhookData?.webhookUrl || '',
      webhookSecret: ''
    },
    validationSchema: Yup.object().shape({
      webhookUrl: Yup.string()
        .required(i18n.getMessage('customer.validation.isRequired'))
        .max(255, i18n.getMessage('customer.validation.maxSize', { size: 255 }))
        .url(i18n.getMessage('customer.validation.isUrl')),
      webhookSecret: Yup.string().max(255, i18n.getMessage('customer.validation.maxSize', { size: 255 }))
    }),
    onSubmit: async (values) => {
      const webhookPayload = {
        webhookUrl: values.webhookUrl
      };

      // Only include webhookSecret if it's not empty
      if (values.webhookSecret && values.webhookSecret.trim() !== '') {
        webhookPayload.webhookSecret = values.webhookSecret;
      }

      if (webhookUrlId) {
        await updateCustomerWebhookUrl(webhookUrlId, webhookPayload);
      } else {
        await createNewCustomerWebhookUrl(webhookPayload);
      }

      await getCurrentCustomerWebhookUrlList();
    }
  });

  const { values, errors, handleSubmit, handleChange } = form;

  const handleDeleteWebhookUrl = async () => {
    await deleteCustomerWebhookUrl(webhookUrlId);
    await getCurrentCustomerWebhookUrlList();
  };

  const handleToggleStatus = (transferType, status) => {
    setTransactionRules((prev) => {
      const currentStatuses = prev[transferType] || [];
      const isStatusEnabled = currentStatuses.includes(status);

      const updatedStatuses = isStatusEnabled
        ? currentStatuses.filter((s) => s !== status) // Remove status
        : [...currentStatuses, status]; // Add status

      return {
        ...prev,
        [transferType]: updatedStatuses
      };
    });
  };

  const handleToggleBatchStatus = (status) => {
    setBatchStatuses((prev) => {
      const isStatusEnabled = prev.includes(status);

      return isStatusEnabled
        ? prev.filter((s) => s !== status) // Remove status
        : [...prev, status]; // Add status
    });
  };

  const handleSaveConfiguration = async () => {
    if (!webhookUrlId) {
      return;
    }

    // Convert rules object back to array for API
    const paymentTransactionRules = Object.entries(transactionRules)
      .filter(([_, enabledStatuses]) => enabledStatuses.length > 0) // Only send rules with selected statuses
      .map(([transferType, enabledStatuses]) => ({
        transferType,
        enabledStatuses
      }));

    const configuration = {
      paymentTransactionRules,
      paymentBatchRule: {
        enabledStatuses: batchStatuses
      }
    };

    await updateWebhookConfiguration(webhookUrlId, configuration);
    await getCurrentCustomerWebhookUrlList();
  };

  if (!isInitialized) {
    return <Loader className={'application-loader webhook-tab-loader'} />;
  }

  return (
    <div className={'webhook-tab-container'}>
      {/* Basic Settings Section */}
      <form className={'webhook-table'} onSubmit={handleSubmit}>
        <Table responsive>
          <tbody className={'webhook-table-body'}>
            <tr className={'webhook-table-row'}>
              <td className={'webhook-table-cell webhook-table-label'}>
                {i18n.getMessage('webhookTab.input.webhookUrl')}
              </td>
              <td className={'webhook-table-cell'}>
                <div className={'table-input-wrapper'}>
                  <Input
                    className={'table-cell-input'}
                    name={'webhookUrl'}
                    value={values.webhookUrl}
                    onChange={handleChange}
                    error={errors?.webhookUrl}
                  />
                </div>
              </td>
            </tr>
            <tr className={'webhook-table-row'}>
              <td className={'webhook-table-cell webhook-table-label'}>
                {i18n.getMessage('webhookTab.input.webhookSecret')}
              </td>
              <td className={'webhook-table-cell'}>
                <div className={'table-input-wrapper'}>
                  <Input
                    className={'table-cell-input'}
                    name={'webhookSecret'}
                    type={'password'}
                    value={values.webhookSecret}
                    onChange={handleChange}
                    error={errors?.webhookSecret}
                    placeholder={i18n.getMessage('webhookTab.input.webhookSecretPlaceholder')}
                  />
                </div>
              </td>
            </tr>
            <tr className={'webhook-table-row'}>
              <td className={'webhook-table-cell'}></td>
              <td className={'webhook-table-cell'}>
                <div className={'webhook-actions-buttons-wrapper'}>
                  {!webhookUrlId ? (
                    <Button
                      className={'webhook-actions-button save-button'}
                      roleType={'submit'}
                      size={'small'}
                      type={'primary'}
                      onClick={() => {}}
                      isDisabled={isLoading}
                    >
                      {i18n.getMessage('webhookTab.button.save')}
                    </Button>
                  ) : (
                    <>
                      <Button
                        className={'webhook-actions-button other-buttons'}
                        roleType={'submit'}
                        size={'small'}
                        type={'primary'}
                        onClick={() => {}}
                        isDisabled={isLoading}
                      >
                        {i18n.getMessage('webhookTab.button.update')}
                      </Button>

                      <Button
                        className={'webhook-actions-button other-buttons'}
                        roleType={'button'}
                        size={'small'}
                        type={'primary'}
                        onClick={handleDeleteWebhookUrl}
                        isDisabled={isLoading}
                      >
                        {i18n.getMessage('webhookTab.button.delete')}
                      </Button>

                      <Button
                        className={'webhook-actions-button test-buttons'}
                        roleType={'button'}
                        size={'small'}
                        type={'primary'}
                        onClick={() => testWebhookUrl(webhookUrlId, TRANSACTION_TYPES.INCOMING)}
                        isDisabled={isLoading}
                      >
                        {i18n.getMessage('webhookTab.button.testIncoming')}
                      </Button>

                      <Button
                        className={'webhook-actions-button test-buttons'}
                        roleType={'button'}
                        size={'small'}
                        type={'primary'}
                        onClick={() => testWebhookUrl(webhookUrlId, TRANSACTION_TYPES.OUTGOING)}
                        isDisabled={isLoading}
                      >
                        {i18n.getMessage('webhookTab.button.testOutgoing')}
                      </Button>
                    </>
                  )}
                </div>
              </td>
            </tr>
          </tbody>
        </Table>
      </form>

      {/* Configuration Section */}
      {webhookUrlId && (
        <div className={'webhook-configuration-section'}>
          <h3 className={'webhook-configuration-title'}>
            {i18n.getMessage('webhookTab.configuration.title')}
          </h3>

          {WEBHOOK_TRANSFER_TYPES_FOR_CONFIG.map((transferType) => (
            <div key={transferType} className={'webhook-rule-section'}>
              <h4 className={'webhook-rule-title'}>
                {i18n.getMessage(`webhookTab.transferType.${transferType}`)}
              </h4>
              <div className={'webhook-rule-statuses'}>
                {WEBHOOK_ENABLED_STATUSES_OPTIONS.map((status) => (
                  <CheckBox
                    key={status}
                    className={'webhook-status-checkbox'}
                    isActive={transactionRules[transferType]?.includes(status) || false}
                    showCheckBox={true}
                    disabled={isLoading}
                    onChange={() => handleToggleStatus(transferType, status)}
                    value={i18n.getMessage(`webhookTab.status.${status}`)}
                  />
                ))}
              </div>
            </div>
          ))}

          {/* Batch Webhooks Section */}
          <div className={'webhook-rule-section'}>
            <h4 className={'webhook-rule-title'}>
              {i18n.getMessage('webhookTab.batchRule.title')}
            </h4>
            <div className={'webhook-rule-statuses'}>
              {WEBHOOK_BATCH_ENABLED_STATUSES_OPTIONS.map((status) => (
                <CheckBox
                  key={status}
                  className={'webhook-status-checkbox'}
                  isActive={batchStatuses.includes(status) || false}
                  showCheckBox={true}
                  disabled={isLoading}
                  onChange={() => handleToggleBatchStatus(status)}
                  value={i18n.getMessage(`webhookTab.status.${status}`)}
                />
              ))}
            </div>
          </div>

          <div className={'webhook-configuration-footer'}>
            <Button
              className={'webhook-save-config-button'}
              roleType={'button'}
              size={'medium'}
              type={'primary'}
              onClick={handleSaveConfiguration}
              isDisabled={isLoading}
            >
              {i18n.getMessage('webhookTab.button.saveConfiguration')}
            </Button>
          </div>
        </div>
      )}

      {/* Direct API Call Token Section */}
      <Table responsive>
        <tbody className={'webhook-table-body'}>
          <tr className={'webhook-table-row'}>
            <td className={'webhook-table-cell'}>{i18n.getMessage('webhookTab.input.directApiCallToken.label')}</td>
            <td className={'webhook-table-cell'}>
              <div className={'table-input-wrapper'}>
                <span className={'table-cell-span'}>{directApiCallToken}</span>
                <div className={'webhook-actions-buttons-wrapper'}>
                  {!isDirectApiCallTokenExist ? (
                    <Button
                      className={'webhook-actions-button generate-button'}
                      roleType={'button'}
                      size={'small'}
                      type={'primary'}
                      onClick={generateDirectApiCallToken}
                      isDisabled={isLoading}
                    >
                      {i18n.getMessage('webhookTab.button.generateToken')}
                    </Button>
                  ) : (
                    <Button
                      className={'webhook-actions-button generate-button'}
                      roleType={'button'}
                      size={'small'}
                      type={'primary'}
                      onClick={revokeDirectApiCallToken}
                      isDisabled={isLoading}
                    >
                      {i18n.getMessage('webhookTab.button.revokeToken')}
                    </Button>
                  )}
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </Table>
    </div>
  );
};

WebhookTab.propTypes = {
  isLoading: PropTypes.bool,
  isInitialized: PropTypes.bool,
  getCurrentCustomerWebhookUrlList: PropTypes.func,
  createNewCustomerWebhookUrl: PropTypes.func,
  updateCustomerWebhookUrl: PropTypes.func,
  deleteCustomerWebhookUrl: PropTypes.func,
  testWebhookUrl: PropTypes.func,
  updateWebhookConfiguration: PropTypes.func,
  customerWebhookUrlList: PropTypes.array,
  directApiCallToken: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
  generateDirectApiCallToken: PropTypes.func,
  revokeDirectApiCallToken: PropTypes.func,
  checkDirectApiCallToken: PropTypes.func,
  isDirectApiCallTokenExist: PropTypes.bool
};

export default inject((stores) => ({}))(observer(WebhookTab));
